import { Component, Input, SimpleChanges } from '@angular/core';
import { ChartDataset, TooltipCallbacks, TooltipItem } from 'chart.js';
import { _DeepPartialObject } from 'chart.js/dist/types/utils';
import { addDays, isSameHour } from 'date-fns';
import { Subscription, forkJoin } from 'rxjs';
import { AvtaleService } from 'src/app/services/avtale/avtale.service';
import { EnodeService } from 'src/app/services/enode/enode.service';
import { SpotService } from 'src/app/services/spot/spot.service';
import { titleTooltipCallback } from 'src/app/tabs/forbruk/graphs/forbruk/charts/forbruk-charts-utils';
import { hasChanged } from 'src/app/utils/change-utils';

@Component({
  selector: 'app-chart',
  templateUrl: './chart.component.html',
  styleUrls: ['./chart.component.scss']
})
export class ChartComponent {
  @Input() startTime: Date;
  @Input() endTime: Date;
  @Input() deadline: Number;
  @Input() locationId: string;
  dataSets: ChartDataset<'bar'>[];
  labels: Date[];
  title: string;
  tooltipCallbacks: _DeepPartialObject<TooltipCallbacks<'bar'>>;
  showLegend: boolean;
  showYTitle: boolean;

  subscription: Subscription;

  prices: {
    value: number;
    date: string;
    hour: string;
  }[];
  constructor(
    private _spotService: SpotService,
    private _avtaleService: AvtaleService,
    private _enodeService: EnodeService
  ) {}

  ionViewWillLeave() {
    this.subscription?.unsubscribe();
  }

  ngOnInit(): void {
    const today = new Date();
    this.subscription = forkJoin({
      tariff: this._enodeService.getTariff(this.locationId),
      tariffConfig: this._enodeService.getTariffConfig(this.locationId),
      avtale: this._avtaleService.getAvtaler()
    }).subscribe({
      next: ({ tariff, tariffConfig, avtale }) => {
        if (tariffConfig) {
          this.prices = tariff.map((entry) => {
            let date = entry.period.toString().substring(0, 8);
            date =
              date.substring(0, 4) +
              '-' +
              date.substring(4, 6) +
              '-' +
              date.substring(6, 8);
            return {
              // Må ha samme format som på spotprisene
              value: entry.tariffValue * 100,
              date: date,
              hour: entry.fromHourMinute.split(':')[0]
            };
          });
          this.getChartData();
        } else {
          this._spotService.getSpotRange(today, addDays(today, 1)).subscribe({
            next: (res) => {
              // Sikkert en bedre måte å gjøre det, men dette funket fort.
              // Vi vil heller hente prisområde på lokasjon til bilen.
              const spot = res.find(
                (x) => x.omrade === avtale.avtaler[0].prisomrade
              );
              // Legg på mva på alle prisene som ikke er NO4
              if (avtale.avtaler[0].prisomrade !== 'NO4') {
                spot.dager.forEach((dag) => {
                  dag.priser.forEach((pris) => {
                    pris.pris = pris.pris * 1.25;
                  });
                });
              }

              this.prices = spot.dager.flatMap((dag) => {
                return dag.priser.map((pris) => {
                  return {
                    value: pris.pris,
                    date: dag.dato,
                    hour: pris.tidspunkt.split(' ')[1].split(':')[0]
                  };
                });
              });
              this.getChartData();
            }
          });
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      !changes.startTime?.firstChange &&
      !changes.endTime?.firstChange &&
      !changes.deadline?.firstChange &&
      !changes.locationId?.firstChange &&
      hasChanged([
        changes.startTime,
        changes.endTime,
        changes.deadline,
        changes.locationId
      ])
    ) {
      this.getChartData();
    }
  }

  labelTooltipCallback = (tooltipItem: TooltipItem<'bar'>, unit: string) => {
    const value = Number(tooltipItem.raw);

    if (value === 0 || isNaN(value) || tooltipItem.dataset.label == 'hide') {
      return '';
    }

    return `Ladekostnad: ${value.toFixed(2).toString().replace('.', ',')} ${unit}`;
  };

  filterPricesWithinAdjustedRange() {
    // Sorter prisene for å få de sammenhengende
    this.prices = this.prices.sort((a, b) => {
      let dateA = new Date(a.date);
      dateA.setHours(+a.hour);
      let dateB = new Date(b.date);
      dateB.setHours(+b.hour);
      return dateA.getTime() - dateB.getTime();
    });

    const startIndex = this.prices.findIndex((x) => {
      let date = new Date(x.date);
      date.setHours(+x.hour);
      return isSameHour(date, this.startTime);
    });

    const endIndex = this.prices.findIndex((x) => {
      let date = new Date(x.date);
      date.setHours(+x.hour);
      return isSameHour(date, this.endTime);
    });

    // Hent ut et subsett av prisene
    this.prices = this.prices.slice(startIndex - 5, endIndex + 10);
  }

  getChartData() {
    this.dataSets = [];
    this.labels = [];
    this.title = 'Ladepriser';
    this.tooltipCallbacks = {
      label: (tt) => this.labelTooltipCallback(tt, 'øre/kWh'),
      title: (tt) => titleTooltipCallback(tt, 'hour', 'øre/kWh')
    };

    this.filterPricesWithinAdjustedRange();

    let yMax = 100;
    const barColors = [];
    const smartChargeBarColors = [];
    const chargeHours: Date[] = [];
    const diff = this.endTime.getTime() - this.startTime.getTime();
    
    for (let i = 0; i <= diff; i += 3600000) {
      const hour = new Date(this.startTime.getTime() + i);
      hour.setMinutes(0);
      chargeHours.push(new Date(hour));
    }
    
    const smartChargingHours =
      this.prices?.map((pris) => {
        const valueHour = pris.hour;
        const timeDato = new Date(pris.date);
        
        timeDato.setHours(+valueHour);

        let label: Date;
        chargeHours.forEach((chargeHour) => {
          if (isSameHour(chargeHour, timeDato)) {
            smartChargeBarColors.push('#26b41b');
            label = chargeHour;
          }
        });
        if (label) {
          return {
            label,
            price: pris.value
          };
        }
      }) || [];
    const timePriserPrimary =
      this.prices?.map((pris) => {
        const valueHour = pris.hour;
        const timeDato = new Date(pris.date);

        timeDato.setHours(+valueHour);
        if (pris.value > yMax) {
          yMax = Math.ceil(pris.value / 100) * 100;
        }
        if (!smartChargingHours.find((x) => isSameHour(x?.label, timeDato))) {
          barColors.push('#002987');
          return {
            label: timeDato,
            price: pris.value
          };
        }
      }) || [];

    // Slår sammen begge arrayene for å få alle labels i grafen
    const mergedArray = timePriserPrimary.map(
      (item, index) => item || smartChargingHours[index]
    );

    const labelExistsInArray = (array, label) =>
      array.some((x) => x && isSameHour(x.label, label));

    // Lag et array med farger for hver verdi
    const backgroundColors = mergedArray.map((item) => {
      if (labelExistsInArray(smartChargingHours, item.label)) {
        return '#26b41b';
      } else if (labelExistsInArray(timePriserPrimary, item.label)) {
        return '#002987';
      }
    });

    this.labels = mergedArray.map((item) => item.label);
    this.dataSets.push({
      data: mergedArray.map((item) => item.price),
      label: 'Unngår smartlading',
      order: 1,
      backgroundColor: backgroundColors.map((color) => color)
    });
    this.dataSets.push({
      data: [],
      label: 'Smartlading',
      backgroundColor: '#26b41b'
    });
  }
}
